// src/components/operationCenter/widgets/OpenInterestWidget.tsx
// Widget wrapper for open interest chart with inline token selection in title

import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { WidgetProps, OpenInterestConfig } from "@/types/dashboard";
import type { TimeRangeOption } from "@/components/common/TimeRangePicker";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { TokenDerivativesProvider } from "@/contexts/token";
import TokenOpenInterestChart from "@/components/tokenPage/derivatives/TokenOpenInterestChart";
import { useTokensCatalog } from "@/hooks/useTokensCatalog";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { MetricKeys } from "@/constants/metrics";
import CompactTokenSelector from "./shared/CompactTokenSelector";

const OpenInterestWidget: React.FC<WidgetProps<OpenInterestConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as OpenInterestConfig;
  const [ticker, setTicker] = useState(config.ticker ?? "BTC");
  const [range, setRange] = useState<TimeRangeOption>("180d");
  const toDays = (r: TimeRangeOption): number | null =>
    r === "All" ? null : Number(r.replace("d", ""));
  const { data: tokenCatalog } = useTokensCatalog();
  const { data: screenerRows, isLoading: isScreenerLoading } =
    useTokensScreenerData();
  const derivativesTickers = useMemo(() => {
    if (!Array.isArray(screenerRows)) return new Set<string>();
    const out = new Set<string>();
    screenerRows.forEach((row) => {
      const ticker = String(row["tokenSymbol"] ?? "").toUpperCase();
      if (!ticker) return;
      const oi = Number(row[MetricKeys.FuturesOI]);
      const frOiw = Number(row[MetricKeys.FuturesFROIW]);
      const frVolw = Number(row[MetricKeys.FuturesFRVolW]);
      if ([oi, frOiw, frVolw].some((v) => Number.isFinite(v))) {
        out.add(ticker);
      }
    });
    return out;
  }, [screenerRows]);
  const availableTokens = useMemo(
    () =>
      (tokenCatalog ?? [])
        .map((token) => token.ticker?.toUpperCase().trim())
        .filter((ticker): ticker is string => Boolean(ticker))
        .filter((ticker) => derivativesTickers.has(ticker)),
    [tokenCatalog, derivativesTickers]
  );
  const selectorDisabled =
    isScreenerLoading || availableTokens.length === 0;

  useEffect(() => {
    if (availableTokens.length === 0) return;
    if (availableTokens.includes(ticker)) return;
    const nextTicker = availableTokens[0] ?? "BTC";
    setTicker(nextTicker);
    onConfigChange?.({ ticker: nextTicker });
  }, [availableTokens, ticker, onConfigChange]);

  const handleTokenChange = useCallback(
    (value: string | string[]) => {
      const newTicker = Array.isArray(value) ? value[0] ?? "BTC" : value;
      setTicker(newTicker);
      onConfigChange?.({ ticker: newTicker });
    },
    [onConfigChange]
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <span className="text-xs uppercase tracking-wide text-slate-300">
          Open Interest
        </span>
        <span className="text-slate-600">·</span>
        <CompactTokenSelector
          value={ticker}
          onChange={handleTokenChange}
          {...(availableTokens.length > 0 ? { tokens: availableTokens } : {})}
          placeholder="Token"
          disabled={selectorDisabled}
        />
        <div className="ml-auto">
          <TimeRangePicker
            options={["30d", "90d", "180d", "365d", "All"] as const}
            value={range}
            onChange={setRange}
            theme="blue"
          />
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <TokenDerivativesProvider ticker={ticker}>
          <TokenOpenInterestChart
            tokenId={ticker}
            hideTitle
            className="h-full"
            containerClassName="h-full w-full"
            externalRangeDays={toDays(range)}
          />
        </TokenDerivativesProvider>
      </div>
    </div>
  );
};

export default OpenInterestWidget;
